// UniformPacker — resolved params → std140 UBO layout + Float32Array packing.
// The single choke point that resolves params to validated defaults (§11.5: never compare a bare
// >/< against a possibly-absent numeric param — packMaterialParams clamps to the schema's min/max).
import {RAMP_MAX_STOPS, type ParamKind, type ParamValue} from "../@MaterialTypes.js";
import type {ResolvedParam} from "../RecipeResolverCore.js";

export interface PackedParamLayout {
	/** Global param key: `${recipeID}/${paramKey}` — unique across the active recipe set. */
	key: string;
	kind: ParamKind;
	/** Offset in FLOAT units (std140: every param 16-byte aligned → 4-float aligned). */
	offsetFloats: number;
	/** Float count: scalar = 1, color = 4 (vec4 .rgb), ramp = 4 * RAMP_MAX_STOPS. */
	floats: number;
	min: number;
	max: number;
	defaultValue: ParamValue;
	/** Set-wide index — the GLSL uniform is `u_p_<index>` (recipe ids never enter the GLSL). */
	index: number;
}

/** Per-param float count by kind (color occupies a vec4 slot to dodge std140 vec3 alignment). */
export function paramFloatCount(kind: ParamKind): number {
	if (kind == "scalar") return 1;
	if (kind == "color") return 4;
	return RAMP_MAX_STOPS * 4; // vec4[6]
}

/** UBO layout with TRUE std140 alignment: scalars → 4B (1 float), vec4/vec4[6] → 16B (4 floats).
 *  Over-aligning every param (the earlier draft) misaligned the GLSL side → garbage params → black output. */
export function computeParamLayouts(params: ResolvedParam[]): PackedParamLayout[] {
	const layouts: PackedParamLayout[] = [];
	let offset = 0; // in floats
	for (const [i, p] of params.entries()) {
		const count = paramFloatCount(p.kind);
		const align = p.kind == "scalar" ? 1 : 4; // std140: float → 4B, vec4 → 16B
		offset = Math.ceil(offset / align) * align;
		layouts.push({
			key: p.key,
			kind: p.kind,
			offsetFloats: offset,
			floats: count,
			min: p.min,
			max: p.max,
			defaultValue: p.default,
			index: i,
		});
		offset += count;
	}
	return layouts;
}

/** Total UBO byte length (16-byte multiple; std140 uniform block minimum). */
export function layoutBytes(layouts: PackedParamLayout[]): number {
	const totalFloats = layouts.length == 0 ? 0 : layouts[layouts.length - 1].offsetFloats + layouts[layouts.length - 1].floats;
	return Math.ceil(totalFloats / 4) * 4 * 4;
}

/** Clamp/validate a scalar into [min, max]; non-finite values resolve to the default (the §11.5 choke point). */
export function resolveScalar(value: number | undefined, layout: PackedParamLayout): number {
	if (typeof value != "number" || !isFinite(value)) return layout.defaultValue as number;
	//return Math.min(layout.max, Math.max(layout.min, value));
	return value; // dev-edited: a user should be able to set any value they want (min/max are just suggestions)
}

/** Pack concrete param values (keyed by global key `${recipeID}/${paramKey}`) into the UBO Float32Array.
 *  Missing keys fall back to the resolved default. Returns a Float32Array of layoutBytes/4 floats. */
export function packMaterialParams(layouts: PackedParamLayout[], values: {[globalKey: string]: ParamValue}): Float32Array {
	const totalFloats = layoutBytes(layouts) / 4;
	const out = new Float32Array(totalFloats);
	for (const l of layouts) {
		const v = values[l.key] ?? l.defaultValue;
		switch (l.kind) {
			case "scalar": {
				out[l.offsetFloats] = resolveScalar(v as number, l);
				break;
			}
			case "color": {
				const c = v as [number, number, number];
				out[l.offsetFloats + 0] = resolveScalar(c?.[0], {...l, min: 0, max: 1});
				out[l.offsetFloats + 1] = resolveScalar(c?.[1], {...l, min: 0, max: 1});
				out[l.offsetFloats + 2] = resolveScalar(c?.[2], {...l, min: 0, max: 1});
				out[l.offsetFloats + 3] = 0; // vec4 .a unused
				break;
			}
			case "ramp": {
				const stops = Array.isArray(v) ? v : (l.defaultValue as number[]);
				if (stops.length % 4 != 0) throw new Error(`[UniformPacker] ramp param "${l.key}" must be t,r,g,b quads (got length ${stops.length})`);
				if (stops.length > RAMP_MAX_STOPS * 4) throw new Error(`[UniformPacker] ramp param "${l.key}" exceeds ${RAMP_MAX_STOPS} stops`);
				for (let i = 0; i < stops.length; i++) out[l.offsetFloats + i] = stops[i];
				// Pad trailing slots with the LAST stop's colour at t = 1.0 (mirror of the literal-ramp
				// path in PrimitiveRegistry.padRampStops) so rampVal terminates identically — an unpadded
				// zero stop would make values past the last real stop map to black.
				if (stops.length > 0) {
					const lastT = stops[stops.length - 4];
					const lastRgb = [stops[stops.length - 3], stops[stops.length - 2], stops[stops.length - 1]];
					for (let i = stops.length; i < RAMP_MAX_STOPS * 4; i += 4) {
						out[l.offsetFloats + i + 0] = Math.max(lastT, 1.0);
						out[l.offsetFloats + i + 1] = lastRgb[0];
						out[l.offsetFloats + i + 2] = lastRgb[1];
						out[l.offsetFloats + i + 3] = lastRgb[2];
					}
				}
				break;
			}
		}
	}
	return out;
}
